import { Component, ViewChild, OnDestroy, HostListener } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EventResponse, EventTypeResponse, CompanyResponse } from '../../../../../../@shared/models/response.module';
import { EventsService } from '../../../../../../@shared/services/events-service';
import { CompanyService } from '../../../../../../@shared/services/company.service';
import { _AQH_EVE_1, _AQH_EVE_2 } from '../../../../../../@shared/models/input.module';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../../../../../@core/services/toast.service';
import { WizardStepReview } from './components/wizard-step-review/wizard-step-review';
import { firstValueFrom } from 'rxjs';

export type EventCategory = 'competition' | 'training' | 'social';

export interface EventCategoryOption {
  id: EventCategory;
  title: string;
  description: string;
  icon: string;
  color: string;
}

export interface WizardStep {
  id: string;
  title: string;
  icon: string;
  required: boolean;
  completed: boolean;
  skipped: boolean;
  excludeFromCategories?: EventCategory[];
}

export type EventDiscipline =
  | 'distance' | 'dressage' | 'eventing' | 'icelandic' | 'driving'
  | 'mounted_games' | 'para_dressage' | 'jumping' | 'vaulting' | 'western'
  | 'working_eq' | 'skeid' | 'equestrian' | 'trotting' | 'gallop'
  | 'polo' | 'academic' | 'exhibition' | 'struk' | 'other';

export interface WizardEventData {
  event_name: string;
  event_description: string;
  event_type_id: number | null;
  discipline: EventDiscipline;
  start_date: string;
  end_date: string;
  registration_deadline: string;
  address: string;
  postal_code: string;
  city: string;
  country_id: number | null;
  default_currency: string;
  logo?: File;
  header?: File;
  category?: EventCategory;
}

@Component({
  selector: 'app-create-event-wizard',
  standalone: false,
  templateUrl: './create-event-wizard.html',
  styleUrl: './create-event-wizard.css'
})
export class CreateEventWizard implements OnDestroy {
  @ViewChild(WizardStepReview) reviewStep: WizardStepReview | undefined;

  companyId: string;
  company: CompanyResponse | null = null;
  event: EventResponse | null = null;
  eventId: number | null = null;
  loading = true;
  saving = false;

  // Track whether user navigated away intentionally (publish/save as draft)
  private navigatedAway = false;

  // Event types for category mapping
  eventTypes: EventTypeResponse[] = [];

  // Category selection
  selectedCategory: EventCategory | null = null;

  categoryOptions: EventCategoryOption[] = [];

  allSteps: WizardStep[] = [];

  steps: WizardStep[] = [];

  currentStepIndex = 0;
  classesCount = 0;
  capacityCount = 0;
  packagesCount = 0;
  schedulesCount = 0;
  showValidation = false;

  eventData: WizardEventData = {
    event_name: '',
    event_description: '',
    event_type_id: null,
    discipline: 'other',
    start_date: '',
    end_date: '',
    registration_deadline: '',
    address: '',
    postal_code: '',
    city: '',
    country_id: null,
    default_currency: 'NOK'
  };

  private formatDateTimeLocal(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  private initializeDefaultDates() {
    const startDate = new Date();
    startDate.setHours(10, 0, 0, 0);
    this.eventData.start_date = this.formatDateTimeLocal(startDate);

    const endDate = new Date();
    endDate.setHours(18, 0, 0, 0);
    this.eventData.end_date = this.formatDateTimeLocal(endDate);
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private eventService: EventsService,
    private companyService: CompanyService,
    private translateService: TranslateService,
    private toastService: ToastService
  ) {}

  private initializeOptions() {
    this.categoryOptions = [
      {
        id: 'competition',
        title: this.translateService.instant('eventWizard.categoryCompetition'),
        description: this.translateService.instant('eventWizard.categoryCompetitionDesc'),
        icon: 'fa-trophy',
        color: '#f39c12'
      },
      {
        id: 'training',
        title: this.translateService.instant('eventWizard.categoryTraining'),
        description: this.translateService.instant('eventWizard.categoryTrainingDesc'),
        icon: 'fa-graduation-cap',
        color: '#3498db'
      },
      {
        id: 'social',
        title: this.translateService.instant('eventWizard.categorySocial'),
        description: this.translateService.instant('eventWizard.categorySocialDesc'),
        icon: 'fa-users',
        color: '#27ae60'
      }
    ];

    this.allSteps = [
      { id: 'basic', title: this.translateService.instant('eventWizard.stepBasicInfo'), icon: 'fa-info-circle', required: true, completed: false, skipped: false },
      { id: 'classes', title: this.translateService.instant('eventWizard.stepClasses'), icon: 'fa-list', required: true, completed: false, skipped: false, excludeFromCategories: ['social', 'training'] },
      { id: 'capacity', title: this.translateService.instant('eventWizard.stepCapacity'), icon: 'fa-calendar-check', required: true, completed: false, skipped: false, excludeFromCategories: ['competition', 'social'] },
      { id: 'packages', title: this.translateService.instant('eventWizard.stepPackages'), icon: 'fa-box-open', required: true, completed: false, skipped: false, excludeFromCategories: ['competition', 'social'] },
      { id: 'schedule', title: this.translateService.instant('eventWizard.stepSchedules'), icon: 'fa-calendar-alt', required: false, completed: false, skipped: false, excludeFromCategories: ['social', 'training'] },
      { id: 'services', title: this.translateService.instant('eventWizard.stepServices'), icon: 'fa-concierge-bell', required: false, completed: false, skipped: false },
      { id: 'contacts', title: this.translateService.instant('eventWizard.stepContacts'), icon: 'fa-address-book', required: false, completed: false, skipped: false },
      { id: 'sponsors', title: this.translateService.instant('eventWizard.stepSponsors'), icon: 'fa-handshake', required: false, completed: false, skipped: false },
      { id: 'payment', title: this.translateService.instant('eventWizard.stepPayment'), icon: 'fa-credit-card', required: false, completed: false, skipped: false },
      { id: 'review', title: this.translateService.instant('eventWizard.stepReview'), icon: 'fa-check-circle', required: true, completed: false, skipped: false }
    ];
  }

  ngOnInit() {
    this.initializeOptions();
    this.initializeDefaultDates();
    this.companyId = this.route.snapshot.paramMap.get('companyId') || '';
    console.log('CreateEventWizard - companyId from route:', this.companyId);
    this.loadCompany();
    this.loadEventTypes();
  }

  loadCompany() {
    this.companyService.getCompanyById({ company_id: this.companyId }).subscribe({
      next: (company) => {
        this.company = company;
        console.log('CreateEventWizard - loaded company:', company?.company_id, company?.company_name, 'is_personal:', company?.is_personal, 'type:', company?.company_type);
        // Set default currency from company
        if (company?.default_currency) {
          this.eventData.default_currency = company.default_currency;
        }
        this.filterCategoryOptions();
        this.loading = false;
      },
      error: (err) => {
        console.error('CreateEventWizard - failed to load company:', this.companyId, err);
        this.loading = false;
      }
    });
  }

  filterCategoryOptions() {
    if (this.company?.company_type === 'personal') {
      // Personal companies can only create social events and trainings
      this.categoryOptions = this.categoryOptions.filter(c => c.id === 'social' || c.id === 'training');
    } else if (this.company?.company_type === 'stable') {
      // Stables cannot create competition events
      this.categoryOptions = this.categoryOptions.filter(c => c.id !== 'competition');
    } else if (this.company?.company_type === 'stores_services') {
      // Stores & Services can only create social events
      this.categoryOptions = this.categoryOptions.filter(c => c.id === 'social');
    } else if (this.company?.company_type === 'riding_club' && !this.company?.subscription_active) {
      // Riding clubs without pro subscription cannot create competition events
      // Mark competition as requiring subscription instead of removing it
      const competitionOption = this.categoryOptions.find(c => c.id === 'competition');
      if (competitionOption) {
        (competitionOption as any).requiresSubscription = true;
      }
    }
  }

  /**
   * Check if a category option requires subscription
   */
  categoryRequiresSubscription(category: EventCategoryOption): boolean {
    return (category as any).requiresSubscription === true;
  }

  loadEventTypes() {
    this.eventService.getEventTypes({}).subscribe({
      next: (types) => {
        this.eventTypes = types || [];
      }
    });
  }

  getEventTypeIdForCategory(category: EventCategory): number {
    // Map categories to their specific event_type_id values
    const categoryToEventTypeId: { [key in EventCategory]: number } = {
      'competition': 0,
      'training': 2,
      'social': 1
    };
    return categoryToEventTypeId[category];
  }

  selectCategory(category: EventCategory) {
    this.selectedCategory = category;
    this.eventData.category = category;
    this.eventData.event_type_id = this.getEventTypeIdForCategory(category);

    // Filter steps based on category
    this.steps = this.allSteps
      .filter(step => !step.excludeFromCategories?.includes(category))
      .map(step => ({ ...step, completed: false, skipped: false }));

    // Skip payment step if Stripe not connected AND custom payment not enabled
    if (this.company && this.company.stripe_account_status !== 'active' && !this.company.custom_payment_enabled) {
      const paymentStep = this.steps.find(s => s.id === 'payment');
      if (paymentStep) {
        paymentStep.skipped = true;
      }
    }

    this.currentStepIndex = 0;
  }

  goBackToCategories() {
    this.selectedCategory = null;
    this.steps = [];
    this.currentStepIndex = 0;
    this.eventId = null;
    this.event = null;
    this.eventData = {
      event_name: '',
      event_description: '',
      event_type_id: null,
      discipline: 'other',
      start_date: '',
      end_date: '',
      registration_deadline: '',
      address: '',
      postal_code: '',
      city: '',
      country_id: null,
      default_currency: this.company?.default_currency || 'NOK'
    };
    this.initializeDefaultDates();
  }

  get currentStep(): WizardStep {
    return this.steps[this.currentStepIndex];
  }

  get isFirstStep(): boolean {
    return this.currentStepIndex === 0;
  }

  get isLastStep(): boolean {
    return this.currentStepIndex === this.steps.length - 1;
  }

  get canGoNext(): boolean {
    const step = this.currentStep;
    if (step.id === 'basic') {
      return this.isBasicInfoValid();
    }
    if (step.id === 'classes' && step.required) {
      return this.classesCount > 0;
    }
    if (step.id === 'capacity' && step.required) {
      return this.capacityCount > 0;
    }
    if (step.id === 'packages' && step.required) {
      return this.packagesCount > 0;
    }
    return true;
  }

  get canSkip(): boolean {
    return !this.currentStep.required;
  }

  isBasicInfoValid(): boolean {
    // Discipline is required for competition and training events, optional for social
    const isDisciplineValid = this.selectedCategory === 'social' || !!this.eventData.discipline;

    return !!(
      this.eventData.event_name &&
      isDisciplineValid &&
      this.eventData.start_date &&
      this.eventData.end_date &&
      this.eventData.address &&
      this.eventData.postal_code &&
      this.eventData.city &&
      this.eventData.country_id &&
      this.isEndDateValid() &&
      this.isRegistrationDeadlineValid()
    );
  }

  isEndDateValid(): boolean {
    if (!this.eventData.end_date) return false;
    if (this.eventData.start_date && this.eventData.end_date) {
      return new Date(this.eventData.end_date) >= new Date(this.eventData.start_date);
    }
    return true;
  }

  isRegistrationDeadlineValid(): boolean {
    if (!this.eventData.registration_deadline) return true;
    if (this.eventData.start_date && this.eventData.registration_deadline) {
      return new Date(this.eventData.registration_deadline) <= new Date(this.eventData.start_date);
    }
    return true;
  }

  getBasicInfoValidationErrors(): string[] {
    const errors: string[] = [];
    if (!this.eventData.event_name) {
      errors.push(this.translateService.instant('validation.eventNameRequired'));
    }
    if (!this.eventData.start_date) {
      errors.push(this.translateService.instant('validation.startDateRequired'));
    }
    if (!this.eventData.end_date) {
      errors.push(this.translateService.instant('validation.endDateRequired'));
    }
    if (this.eventData.start_date && this.eventData.end_date && !this.isEndDateValid()) {
      errors.push(this.translateService.instant('validation.endDateBeforeStartDate'));
    }
    if (!this.isRegistrationDeadlineValid()) {
      errors.push(this.translateService.instant('validation.deadlineAfterStartDate'));
    }
    if (!this.eventData.address) {
      errors.push(this.translateService.instant('validation.addressRequired'));
    }
    if (!this.eventData.postal_code) {
      errors.push(this.translateService.instant('validation.postalCodeRequired'));
    }
    if (!this.eventData.city) {
      errors.push(this.translateService.instant('validation.cityRequired'));
    }
    if (!this.eventData.country_id) {
      errors.push(this.translateService.instant('validation.countryRequired'));
    }
    return errors;
  }

  goToStep(index: number) {
    // Allow navigation to:
    // 1. Any previous step (going backwards)
    // 2. The next step if the previous step is completed (going forward)
    // 3. Any step if we're on the review step (last step)
    if (index < 0 || index >= this.steps.length) {
      return; // Invalid index
    }

    const isGoingBack = index < this.currentStepIndex;
    const isOnReviewStep = this.currentStep?.id === 'review';
    const previousStepCompleted = index === 0 || this.steps[index - 1]?.completed;

    if (isGoingBack || isOnReviewStep || previousStepCompleted) {
      this.currentStepIndex = index;
    }
  }

  async nextStep() {
    const step = this.currentStep;

    // Validate current step before proceeding
    if (step.id === 'basic') {
      this.showValidation = true;
      if (!this.isBasicInfoValid()) {
        const errors = this.getBasicInfoValidationErrors();
        errors.forEach(error => {
          this.toastService.error(error);
        });
        return;
      }
    }

    if (step.id === 'classes' && step.required && this.classesCount === 0) {
      this.toastService.error(this.translateService.instant('validation.atLeastOneClassRequired'));
      return;
    }

    if (step.id === 'capacity' && step.required && this.capacityCount === 0) {
      this.toastService.error(this.translateService.instant('validation.capacityRequired'));
      return;
    }

    if (step.id === 'packages' && step.required && this.packagesCount === 0) {
      this.toastService.error(this.translateService.instant('validation.atLeastOnePackageRequired'));
      return;
    }

    if (this.currentStepIndex === 0) {
      if (!this.eventId) {
        // Create event in step 1
        await this.createEvent();
      } else {
        // Update event if it already exists (user went back and changed data)
        await this.updateEvent();
      }
    }

    if (this.currentStepIndex < this.steps.length - 1) {
      this.steps[this.currentStepIndex].completed = true;
      this.showValidation = false;

      // Find next non-skipped step
      let nextIndex = this.currentStepIndex + 1;
      while (nextIndex < this.steps.length && this.steps[nextIndex].skipped) {
        this.steps[nextIndex].completed = true;
        nextIndex++;
      }
      this.currentStepIndex = nextIndex;
    }
  }

  previousStep() {
    if (this.currentStepIndex > 0) {
      // Find previous non-skipped step
      let prevIndex = this.currentStepIndex - 1;
      while (prevIndex > 0 && this.steps[prevIndex].skipped) {
        prevIndex--;
      }
      this.currentStepIndex = prevIndex;
    }
  }

  skipStep() {
    if (this.canSkip) {
      this.steps[this.currentStepIndex].skipped = true;
      this.steps[this.currentStepIndex].completed = true;
      this.nextStep();
    }
  }

  async createEvent() {
    if (!this.companyId) {
      this.toastService.error(this.translateService.instant('eventWizard.missingCompanyError'));
      return;
    }
    this.saving = true;
    const params: _AQH_EVE_1 = {
      event_name: this.eventData.event_name,
      event_description: this.eventData.event_description,
      event_type_id: this.eventData.event_type_id || this.getEventTypeIdForCategory(this.selectedCategory || 'competition'),
      category: this.selectedCategory || 'competition',
      discipline: this.eventData.discipline || 'other',
      start_date: this.eventData.start_date,
      end_date: this.eventData.end_date,
      registration_deadline: this.eventData.registration_deadline,
      address: this.eventData.address,
      postal_code: this.eventData.postal_code,
      city: this.eventData.city,
      country: '',  // Legacy field - we now use country_id
      country_id: this.eventData.country_id,
      default_currency: this.eventData.default_currency
    };

    try {
      const res = await firstValueFrom(this.eventService.addEvent(params, this.companyId));
      if (res?.data) {
        this.eventId = res.data.event_id;
        this.event = res.data;

        // Upload images if provided
        if (this.eventData.logo) {
          await firstValueFrom(this.eventService.editEventFiles({
            event_id: this.eventId,
            image_type: 'logo',
            image: this.eventData.logo
          }));
        }
        if (this.eventData.header) {
          await firstValueFrom(this.eventService.editEventFiles({
            event_id: this.eventId,
            image_type: 'header',
            image: this.eventData.header
          }));
        }
        // Note: Backend automatically adds the event creator as organiser contact
      }
    } finally {
      this.saving = false;
    }
  }

  async updateEvent() {
    if (!this.eventId) return;

    this.saving = true;
    const params: _AQH_EVE_2 = {
      event_id: this.eventId,
      event_name: this.eventData.event_name,
      event_description: this.eventData.event_description,
      event_type_id: this.eventData.event_type_id || undefined,
      discipline: this.eventData.discipline || 'other',
      start_date: this.eventData.start_date,
      end_date: this.eventData.end_date,
      registration_deadline: this.eventData.registration_deadline || undefined,
      address: this.eventData.address,
      postal_code: this.eventData.postal_code,
      city: this.eventData.city,
      country: '',  // Legacy field - we now use country_id
      country_id: this.eventData.country_id,
      default_currency: this.eventData.default_currency
    };

    try {
      const res = await firstValueFrom(this.eventService.editEvent(params, this.companyId));
      if (res?.data) {
        this.event = res.data;
      }

      // Upload images if provided (new images)
      if (this.eventData.logo) {
        await firstValueFrom(this.eventService.editEventFiles({
          event_id: this.eventId,
          image_type: 'logo',
          image: this.eventData.logo
        }));
      }
      if (this.eventData.header) {
        await firstValueFrom(this.eventService.editEventFiles({
          event_id: this.eventId,
          image_type: 'header',
          image: this.eventData.header
        }));
      }
    } finally {
      this.saving = false;
    }
  }

  onEventUpdated(event: EventResponse) {
    this.event = event;
  }

  onClassesChanged(count: number) {
    this.classesCount = count;
  }

  onCapacityChanged(count: number) {
    this.capacityCount = count;
  }

  onPackagesChanged(count: number) {
    this.packagesCount = count;
  }

  onSchedulesChanged(count: number) {
    this.schedulesCount = count;
  }

  onBasicInfoChange(data: Partial<WizardEventData>) {
    this.eventData = { ...this.eventData, ...data };
  }

  get canPublish(): boolean {
    // Check if review step is available and ready to publish
    if (this.reviewStep) {
      return this.reviewStep.isReadyToPublish && !this.reviewStep.loading;
    }
    return false;
  }

  @HostListener('window:beforeunload')
  onBeforeUnload() {
    // If user closes the browser tab/window, fire a delete request for the draft
    if (this.eventId && !this.navigatedAway) {
      this.eventService.deleteEvent({ event_id: this.eventId }, this.companyId).subscribe();
    }
  }

  ngOnDestroy() {
    // If component is destroyed without intentional navigation (e.g. browser back),
    // delete the draft event
    if (this.eventId && !this.navigatedAway) {
      this.eventService.deleteEvent({ event_id: this.eventId }, this.companyId).subscribe();
    }
  }

  async publishEvent() {
    if (!this.eventId) return;

    // Check if event is ready to publish
    if (!this.canPublish) {
      const message = this.reviewStep?.publishRequirementMessage || this.translateService.instant('eventWizard.reviewRequirements');
      this.toastService.error(message);
      return;
    }

    this.saving = true;
    try {
      this.navigatedAway = true;
      await firstValueFrom(this.eventService.publishEvent(this.eventId, this.companyId));
      this.router.navigate(['/company-profile/manage-event', this.eventId]);
    } catch {
      this.navigatedAway = false;
      this.saving = false;
    }
  }

  saveAsDraft() {
    if (this.eventId) {
      this.navigatedAway = true;
      this.router.navigate(['/company-profile/manage-event', this.eventId]);
    }
  }

  async cancel() {
    // If an event was already created, delete the draft
    if (this.eventId) {
      try {
        await firstValueFrom(this.eventService.deleteEvent({ event_id: this.eventId }, this.companyId));
        this.navigatedAway = true;
      } catch (error) {
        // navigatedAway stays false so ngOnDestroy can retry the delete
        console.error('Failed to delete draft event:', error);
      }
    } else {
      this.navigatedAway = true;
    }
    this.router.navigate(['/company-profile'], { queryParams: { tab: 'events' } });
  }

  getStepClass(step: WizardStep, index: number): string {
    if (step.completed && !step.skipped) return 'completed';
    if (step.skipped) return 'skipped';
    if (index === this.currentStepIndex) return 'active';
    return '';
  }
}
